import React, {useMemo, useState, useEffect, useCallback} from "react";
import {useUser} from "../../../util/user";
import Noti from "../../../util/noti";
import {styleDetails, optionsTable} from "./styleTable";
import {SITEOTHERSCOLS} from "./../../../util/columns";
import MaterialTable from "material-table";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import {useUserProps} from "./../../../util/userDBContext";
import Loader from "./../../general/Loader";
import { useRouteWithData } from "../../../util/router";

function ConstructionTable (props) {
	const user = useUser();
	const siteView = user.roles.includes("administration:SiteCreation:_view");
	const siteCons = user.roles.includes("administration:SiteCreation:_editCons");
	const siteAdmin = user.roles.includes("administration:SiteCreation:_admin");

	const { openLink } = useRouteWithData();

	const [ready, setReady] = useState(false); // table is ready or not
	const {loggedCountry} = useUserProps();

	const [data, setData] = useState([]); // data for table
	const [acts, setActs] = useState([]); // actions for table

	const [needLoader, setNeedLoader] = useState(false); // need loader or not
	const [showLoader, setShowLoader] = useState({position: "relative"});

	const otherscols = useMemo(() => SITEOTHERSCOLS, []); // columns for PM and ACCT

	const loaderMounted = (boo) => {
		if (boo) {
			setShowLoader({position: "fixed"});
			setNeedLoader(true);
		} else {
			setShowLoader({position: "relative"});
			setNeedLoader(false);
		}
	};

	function compareStatus (a, b) {
		if (a.sorting < b.sorting) return -1;
		if (a.sorting > b.sorting) return 1;
		return 0;
	}

	function notiCatch (err) {
		const msg = err.response.data.error ? err.response.data.error : err.response.statusText;
		return Noti.create({type: "e", msg});
	}

	/**
	 * Get data from back-end for Construction Site Table
	 */
	const fetchData = useCallback(async () => {
		const uri = siteAdmin ? `/site_creation/savedsites/pm/null` : `/site_creation/savedsites/pm/${loggedCountry}`;

		const conSite = await GYAxios.get(uri).catch((error) => console.log(error));

		if (conSite?.data) {
			await conSite.data.forEach((v) => {
				v.dateCreated = moment(v.dateCreated).format("YYYY-MM-DD");

				if (v.isSent === 0 && v.isRejected === 2) {
					v.site_status = "Saved";
					v.sorting = "a";
				} else if (v.isSent === 1 && v.isRejected === 2) {
					v.site_status = "Waiting for Department Manager's Approval";
					v.sorting = "b";
				} else if (v.isSent === 1 && v.isRejected === 1) {
					v.site_status = `Rejected because ${v.rejectedRemark}`;
					v.sorting = "c";
				} else if (v.isSent === 1 && v.isRejected === 0 && v.exu_site === null) {
					v.site_status = "Approved. Waiting for IT's creation";
					v.sorting = "d";
				} else if (v.isSent === 1 && v.isRejected === 0 && v.exu_site !== null) {
					v.site_status = "Created";
					v.sorting = "e";
				}
			});

			const tempActions = [];
			await siteView && tempActions.push(() => {
				return {
					icon: "visibility",
					tooltip: <p style={{fontSize: "2em"}}>View</p>,
					onClick: (e, rowData) => passInfoToForm(e, {...rowData}),
				};
			});


			await siteCons && tempActions.push(
				(rowData) => {
					return rowData.GPS !== null &&
						rowData.SF_id !== null &&
						rowData.address !== null &&
						rowData.city !== null &&
						rowData.cod !== null &&
						rowData.contract_id !== null &&
						rowData.contract_duration !== null &&
						rowData.country !== null &&
						rowData.nb_grid_meter !== null &&
						rowData.nb_amb_temp !== null &&
						rowData.site_name !== null &&
						rowData.solo !== null &&
						rowData.sync !== null &&
						rowData.temp_client_name !== null &&
						((rowData.nb_irr_sensors !== null &&
						rowData.nb_inj_points !== null &&
						rowData.nb_inverter !== null &&
						rowData.inverter_id !== null &&
						rowData.EDMI_meter !== null &&
						rowData.nb_solar_modules !== null &&
						rowData.temp_solar_module !== null &&
						rowData.nb_mod_temp !== null &&
						rowData.power !== null) || rowData.is_no_PV) &&
						(rowData.is_BESS ? (
							rowData.BESS_capacity !== null &&
							rowData.BESS_model !== null &&
							rowData.BESS_nb_cabinets > 0 &&
							rowData.is_no_PV !== null &&
							rowData.BESS_meter_type !== null
						) : true) &&
						rowData.isSent === 0 &&
						rowData.isRejected === 2 &&
						(rowData.requestedBy === user.email || rowData.lastUpdatedBy === user.email)
						? {
							icon: "send",
							tooltip: <p style={{fontSize: "2em"}}>Send to Department Manager for Approval</p>,
							onClick: (e, rowData) => {sendEmail(rowData);},
						} : null;
				},
				(rowData) => {
					return rowData.isSent === 0 && rowData.isRejected === 2 && (rowData.requestedBy === user.email || rowData.lastUpdatedBy === user.email)
						? {
							icon: "delete",
							tooltip: <p style={{fontSize: "2em"}}>Delete Site</p>,
							onClick: (e, rowData) => deleteSite(rowData),
						} : null;
				}
			);

			return {
				data: conSite.data.sort((a, b) => compareStatus(a, b) || moment(b.dateCreated).diff(moment(a.dateCreated))),
				actions: tempActions,
			};
		} else {
			const msg = "Error fetching data from server.";
			Noti.create({type: "e", msg});
			return {};
		}
	}, [loggedCountry]);

	const sendEmail = async (rowData) => {
		const question = "Are you sure you want to send this site to Department Manager for approval?";
		const confirm = window.confirm(question);
		if (!confirm) return;

		loaderMounted(true);
		try {
			await GYAxios.post("/site_creation/sendEmailToDepManagerForApproval", {...rowData, role: "administration:SiteCreation:_approvalCons", email: user.email, isSent: 1}).then((res) => {
				Noti.create({type: 's', msg: res.data.message});
				const timer = setTimeout(() => {
					loaderMounted(false);
					reload();
				}, 1000);
				return () => clearTimeout(timer);
			}).catch((error) => {
				Noti.create({type: 'e', msg: 'Something is wrong.'});
				setNeedLoader(false);
				return;
			});
		} catch (err) {
			Noti.create({type: 'e', msg: 'Something is wrong.'});
			setNeedLoader(false);
		}
	};

	const deleteSite = async (rowData) => {
		const question = "Are you sure you want to delete this site?";
		const confirm = window.confirm(question);
		if (!confirm) return;

		loaderMounted(true);
		try {
			const uri = `/site_creation/delete/site_cons/${rowData.site_id}/${user.email}`;
			await GYAxios.patch(uri).then((res) => {
				Noti.create({type: 's', msg: res.data.message});
				const timer = setTimeout(() => {
					loaderMounted(false);
					reload();
				}, 1000);
				return () => clearTimeout(timer);
			}).catch((error) => {
				Noti.create({type: 'e', msg: 'Something is wrong.'});
				setNeedLoader(false);
				return;
			});
		} catch (err) {
			Noti.create({type: 'e', msg: 'Something is wrong.'});
			setNeedLoader(false);
		}
	};

	/**
	 * Passing data of selected site to SiteForm
	 * through SiteCreation
	 * @param {Object} data selected site
	 */
	function passInfoToForm (e, data) {
		const payload = { siteInfo: data };
		openLink(e, '/solar/administration/site-creation/manage-site/construction-form', payload);
	}

	function reload () {
		setReady(false);
		const getData = async () => {
			const {data, actions} = await fetchData();
			setData(data);
			setActs(actions);
		};
		getData().then(() => setReady(true));
	}

	useEffect(() => {
		const getData = async () => {
			const {data, actions} = await fetchData();
			setData(data);
			setActs(actions);
		};
		getData().then(() => setReady(true));
	}, [fetchData, ready]);

	return (
		(siteView) && <>
			<MaterialTable
				title={
					<div>
						<h5>
							Construction Sites Table{" "}
							<span
								className="	glyphicon glyphicon-refresh"
								onClick={() => reload()}
							></span>
						</h5>
					</div>
				}
				columns={otherscols}
				isLoading={!ready}
				data={data}
				actions={acts}
				options={optionsTable(data)}
				style={{marginBottom: "10px"}}
				detailPanel={(rowData) => {
					return (
						<ul style={styleDetails}>
							{rowData.rejectedRemark !== null ? <li>REJECTED REMARK: {rowData.rejectedRemark}</li> : ""}
							<li>Last updated by: {rowData.lastUpdatedBy || "-"}</li>
							<li>
								Last updated at:{" "}
								{rowData.lastUpdatedAt ? moment(rowData.lastUpdatedAt).format("DD/MM/YYYY") : "-"}
							</li>
						</ul>
					);
				}}
			/>
			{needLoader && <div className="overlay translucent" style={showLoader}>
				<Loader
					translucent={true}
					customStyle={{
						width: "150px", height: "150px", position: "fixed", left: "50%", top: "35%",
					}}
				/>
			</div>}
		</>
	);
}

export default React.memo(ConstructionTable);
